import React, { useState, useEffect } from 'react';
import { 
  DollarSign, Clock, CheckCircle, XCircle, 
  AlertCircle, Search, Filter, Calendar,
  User, FileText, MessageSquare, TrendingUp, Upload,
  CheckCircle2, Download, Eye, MoreHorizontal, Edit, Trash2, Check, X
} from 'lucide-react';
import { commissionsService, agencyService, commissionMessagesService } from '../../services';
import { useResponsive } from '../../hooks/useResponsive';
import { useAuth } from '../../contexts/AuthContext';
import travelieTheme from '../../styles/travelieTheme';
import AdminCommissionMessagingModal from './AdminCommissionMessagingModal';
import CommissionUploadModal from './CommissionUploadModal';
import CommissionExportModal from './CommissionExportModal';
import EditableField from './EditableField';
import EditReasonModal from './EditReasonModal';

const CommissionsManagement = () => {
  const { isMobile, isTablet, isDesktopOrWide } = useResponsive();
  const { user } = useAuth();
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    total: 0,
    totalPages: 0,
    limit: 50
  });
  const [selectedCommission, setSelectedCommission] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showMessagingModal, setShowMessagingModal] = useState(false);
  const [selectedCommissionForMessaging, setSelectedCommissionForMessaging] = useState(null);
  const [filters, setFilters] = useState({
    status: 'all',
    agent: 'all',
    dateRange: 'all'
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [stats, setStats] = useState({
    pending: 0,
    approved: 0,
    denied: 0,
    onHold: 0,
    needsReply: 0,
    paid: 0,
    processing: 0,
    totalAmount: 0,
    paidAmount: 0,
    avgCommission: 0
  });
  const [agents, setAgents] = useState([]);
  const [messageSent, setMessageSent] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showPayConfirmModal, setShowPayConfirmModal] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [paymentSummaryLoading, setPaymentSummaryLoading] = useState(false);
  const [paymentSummary, setPaymentSummary] = useState(null);
  const [selectedCommissions, setSelectedCommissions] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [originalData, setOriginalData] = useState({});
  const [editingFields, setEditingFields] = useState({});
  const [changedFields, setChangedFields] = useState([]);
  const [showEditReasonModal, setShowEditReasonModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [prefilledMessage, setPrefilledMessage] = useState('');
  const [showExportModal, setShowExportModal] = useState(false);
  const [reviewBonusSettings, setReviewBonusSettings] = useState(null);
  const [reviewBonusClaims, setReviewBonusClaims] = useState([]);
  const [reviewBonusLoading, setReviewBonusLoading] = useState(false);

  // TravelieTheme Helper Functions
  const getButtonStyle = (type = 'primary', size = 'medium', disabled = false) => {
    const baseStyle = {
      padding: size === 'small' ? `${travelieTheme.spacing.xs} ${travelieTheme.spacing.sm}` : 
               size === 'large' ? `${travelieTheme.spacing.md} ${travelieTheme.spacing.lg}` :
               `${travelieTheme.spacing.sm} ${travelieTheme.spacing.md}`,
      fontSize: size === 'small' ? travelieTheme.typography.fontSize.xs : 
                size === 'large' ? travelieTheme.typography.fontSize.base :
                travelieTheme.typography.fontSize.sm,
      fontWeight: travelieTheme.typography.fontWeight.medium,
      borderRadius: travelieTheme.components.card.borderRadius,
      border: 'none',
      cursor: disabled ? 'not-allowed' : 'pointer',
      transition: `all ${travelieTheme.transitions.fast}`,
      opacity: disabled ? 0.5 : 1,
      display: 'inline-flex',
      alignItems: 'center',
      gap: travelieTheme.spacing.xs,
      whiteSpace: 'nowrap'
    };

    const styles = {
      primary: { ...baseStyle, background: travelieTheme.colors.primary.main, color: '#FFFFFF' },
      secondary: { ...baseStyle, background: travelieTheme.colors.neutral.border, color: travelieTheme.colors.text.primary },
      danger: { ...baseStyle, background: travelieTheme.colors.status.error, color: '#FFFFFF' },
      success: { ...baseStyle, background: travelieTheme.colors.status.success, color: '#FFFFFF' },
      info: { ...baseStyle, background: '#3B9EFF', color: '#FFFFFF' },
      warning: { ...baseStyle, background: travelieTheme.colors.status.warning, color: '#FFFFFF' },
      purple: { ...baseStyle, background: '#8B5CF6', color: '#FFFFFF' },
      link: { ...baseStyle, background: 'transparent', color: travelieTheme.colors.primary.main, padding: '0' }
    };

    return styles[type] || styles.primary;
  };

  const inputStyle = {
    width: '100%',
    padding: travelieTheme.spacing.sm,
    fontSize: travelieTheme.typography.fontSize.sm,
    border: `1px solid ${travelieTheme.colors.neutral.border}`,
    borderRadius: travelieTheme.components.card.borderRadius,
    outline: 'none',
    transition: `border-color ${travelieTheme.transitions.fast}`
  };

  const getStatCardStyle = () => ({
    background: '#FFFFFF',
    borderRadius: travelieTheme.components.card.borderRadius,
    padding: travelieTheme.spacing.lg,
    boxShadow: travelieTheme.components.card.shadow,
    transition: `all ${travelieTheme.transitions.normal}`,
    cursor: 'default'
  });

  useEffect(() => {
    loadAgents();
    loadReviewBonusData();
  }, []);

  const loadAgents = async () => {
    try {
      const data = await agencyService.getAgents();
      setAgents(data.agents || []);
    } catch (error) {
      console.error('Failed to load agents:', error);
    }
  };

  const loadReviewBonusData = async () => {
    try {
      const [settings, claims] = await Promise.all([
        commissionsService.getReviewBonusSettings(),
        commissionsService.getReviewBonusClaims()
      ]);
      setReviewBonusSettings(settings);
      setReviewBonusClaims(claims || []);
    } catch (error) {
      if (error.response?.status !== 403) console.error('Failed to load review bonus data:', error);
    }
  };

  const handleReviewBonusToggle = async () => {
    if (!reviewBonusSettings) return;
    setReviewBonusLoading(true);
    try {
      const result = await commissionsService.updateReviewBonusSettings(!reviewBonusSettings.enabled);
      setReviewBonusSettings(prev => ({ ...prev, enabled: result.enabled }));
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to update review bonus promotion.');
    } finally {
      setReviewBonusLoading(false);
    }
  };

  const handleReviewBonusDecision = async (claim, status) => {
    const rejectionReason = status === 'rejected' ? window.prompt('Why is this proof being rejected? The agent will see this message.') : '';
    if (status === 'rejected' && !rejectionReason?.trim()) return;
    setReviewBonusLoading(true);
    try {
      await commissionsService.updateReviewBonusClaim(claim.id, status, rejectionReason);
      await Promise.all([loadReviewBonusData(), loadCommissions(pagination.page)]);
      if (selectedCommission?.id === claim.commission_id) {
        setSelectedCommission(prev => ({
          ...prev,
          review_bonus_status: status,
          review_bonus_applied: status === 'approved' ? prev.review_bonus_applied : 0,
          review_bonus_rejection_reason: status === 'rejected' ? rejectionReason : null,
          agent_commission: status === 'rejected' ? (Number(prev.agent_commission) - Number(claim.bonus_amount)).toFixed(2) : prev.agent_commission
        }));
      }
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to update review bonus claim.');
    } finally {
      setReviewBonusLoading(false);
    }
  };

  const viewReviewBonusScreenshot = async (claimId) => {
    try {
      const url = await commissionsService.getReviewBonusScreenshot(claimId);
      window.open(url, '_blank', 'noopener,noreferrer');
      window.setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to open the review screenshot.');
    }
  };

  const loadCommissions = async (page = 1) => {
    try {
      setLoading(true);
      
      // Use agency endpoint for agency-wide commission view
      const data = await agencyService.getCommissions({
        status: filters.status !== 'all' ? filters.status : undefined,
        agentId: filters.agent !== 'all' ? filters.agent : undefined,
        dateRange: filters.dateRange !== 'all' ? filters.dateRange : undefined,
        search: searchTerm.trim() || undefined,
        page: page,
        limit: 50
      });
      
      setCommissions(data.commissions || []);
      
      // Update pagination state
      setPagination({
        page: page,
        total: data.pagination?.total || 0,
        totalPages: Math.ceil((data.pagination?.total || 0) / 50),
        limit: 50
      });
      
      // Stats are returned by the API
      if (data.stats) {
        setStats({
          pending: data.stats.pending || 0,
          approved: data.stats.approved || 0,
          denied: data.stats.denied || 0,
          onHold: data.stats.onHold || 0,
          needsReply: data.stats.needsReply || 0,
          paid: data.stats.paid || 0,
          processing: data.stats.processing || 0,
          totalAmount: data.stats.totalAmount || 0,
          paidAmount: data.stats.paidAmount || 0,
          avgCommission: data.stats.avgCommission || 0
        });
      }
    } catch (error) {
      console.error('Failed to load commissions:', error);
    } finally {
      setLoading(false);
    }
  };

  // Date formatting function to avoid timezone issues
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';
    // Use string splitting to avoid timezone conversion issues
    const datePart = dateString.split('T')[0]; // Get YYYY-MM-DD part
    const [year, month, day] = datePart.split('-');
    const date = new Date(year, month - 1, day); // Create date in local timezone
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Page navigation handlers
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages && newPage !== pagination.page) {
      loadCommissions(newPage);
    }
  };

  const handleUpdateStatus = async (id, status, notes) => {
    try {
      await commissionsService.updateStatus(id, status, notes);
      await loadCommissions(pagination.page); // ✅ FIX: Preserve current page
      setShowDetailModal(false);
    } catch (error) {
      console.error('Failed to update commission status:', error);
      alert(error.response?.data?.error || 'Failed to update commission status.');
    }
  };

  const handleUpdateNotes = async (id, notes) => {
    try {
      await commissionsService.updateNotes(id, notes);
      await loadCommissions(pagination.page); // ✅ FIX: Preserve current page
      // Update the selected commission with new notes to reflect changes immediately
      setSelectedCommission(prev => ({ ...prev, admin_notes: notes }));
    } catch (error) {
      console.error('Failed to update commission notes:', error);
    }
  };

  // Bulk selection handlers
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedCommissions([]);
      setSelectAll(false);
      setShowBulkActions(false);
    } else {
      const allIds = commissions.map(commission => commission.id);
      setSelectedCommissions(allIds);
      setSelectAll(true);
      setShowBulkActions(true);
    }
  };

  const handleSelectCommission = (commissionId) => {
    const isSelected = selectedCommissions.includes(commissionId);
    let newSelected;
    
    if (isSelected) {
      newSelected = selectedCommissions.filter(id => id !== commissionId);
    } else {
      newSelected = [...selectedCommissions, commissionId];
    }
    
    setSelectedCommissions(newSelected);
    setSelectAll(newSelected.length === commissions.length);
    setShowBulkActions(newSelected.length > 0);
  };

  const handleBulkStatusUpdate = async (status, notes = '') => {
    if (selectedCommissions.length === 0) return;
    
    try {
      await commissionsService.bulkUpdateStatus(selectedCommissions, status, notes);
      await loadCommissions(pagination.page); // ✅ FIX: Preserve current page
      setSelectedCommissions([]);
      setSelectAll(false);
      setShowBulkActions(false);
    } catch (error) {
      console.error('Failed to bulk update commission status:', error);
    }
  };

  const handleMessageSent = () => {
    setMessageSent(true);
    loadCommissions(pagination.page); // ✅ FIX: Preserve current page
  };

  const handleUploadComplete = () => {
    loadCommissions(pagination.page); // ✅ FIX: Preserve current page
  };

  // Helper function to render field with original data if edited
  const renderFieldWithOriginal = (label, currentValue, fieldName) => {
    const originalValue = selectedCommission?.original_data?.[fieldName];
    const isEdited = originalValue !== null && originalValue !== undefined && originalValue !== currentValue;
    
    // Format original value for date fields
    const formatOriginalValue = (value, field) => {
      if ((field === 'booking_date' || field === 'travel_date') && value) {
        try {
          return new Date(value).toLocaleDateString();
        } catch (e) {
          return value; // Return original if parsing fails
        }
      }
      return value;
    };
    
    return (
      <div className="mb-2">
        <p className="text-sm">
          <span className="font-medium">{label}:</span> {currentValue || <span className="text-gray-400 italic">Not provided</span>}
          {isEdited && (
            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
              Edited
            </span>
          )}
        </p>
        {isEdited && originalValue && (
          <p className="text-xs text-gray-500 ml-0 mt-1">
            <span className="font-medium">Original Data:</span> {formatOriginalValue(originalValue, fieldName)}
          </p>
        )}
      </div>
    );
  };

  const handleOpenPayConfirmation = async () => {
    setShowPayConfirmModal(true);
    setPaymentSummary(null);
    setPaymentSummaryLoading(true);
    try {
      setPaymentSummary(await commissionsService.getApprovedPaymentSummary());
    } catch (error) {
      console.error('Payment export summary error:', error);
      setShowPayConfirmModal(false);
      if (error.response?.status === 404) alert('No approved commissions found for payment.');
      else if (error.response?.status === 409) alert(error.response?.data?.error || 'Resolve pending review bonus claims first.');
      else alert('Failed to load the payment summary. Please try again.');
    } finally {
      setPaymentSummaryLoading(false);
    }
  };

  const handlePayApprovedCommissions = async () => {
    if (!paymentSummary?.commissionCount) return;
    setPaymentProcessing(true);
    try {
      const response = await commissionsService.exportApprovedForPayment();
      
      // Create download link
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Extract filename from response headers or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'approved_commissions_payment.csv';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      // Reload commissions to reflect status changes
      await loadCommissions(pagination.page); // ✅ FIX: Preserve current page
      
      setShowPayConfirmModal(false);
      setPaymentSummary(null);
      alert(`Payment export completed. ${paymentSummary.commissionCount} commission(s) were marked Paid and ${paymentSummary.emailCount} consolidated agent email(s) were queued.`);
      
    } catch (error) {
      console.error('Payment export error:', error);
      if (error.response?.status === 404) {
        alert('No approved commissions found for payment.');
      } else {
        alert('Failed to export commissions for payment. Please try again.');
      }
    } finally {
      setPaymentProcessing(false);
    }
  };

  const handleEditCommission = () => {
    setIsEditMode(true);
    // Initialize edit form data with current values
    setEditFormData({
      first_name: selectedCommission.first_name || '',
      last_name: selectedCommission.last_name || '',
      email: selectedCommission.email || '',
      phone: selectedCommission.phone || '',
      reservation_number: selectedCommission.reservation_number || '',
      guest_first_name: selectedCommission.guest_first_name || '',
      guest_last_name: selectedCommission.guest_last_name || '',
      supplier: selectedCommission.supplier || '',
      booking_date: selectedCommission.booking_date ? selectedCommission.booking_date.split('T')[0] : '',
      travel_date: selectedCommission.travel_date ? selectedCommission.travel_date.split('T')[0] : '',
      gross_commission: selectedCommission.gross_commission || '',
      agent_commission: selectedCommission.agent_commission || '',
      notes: selectedCommission.notes || ''
    });
    
    // Store original data for comparison
    setOriginalData({
      first_name: selectedCommission.original_data?.first_name || selectedCommission.first_name || '',
      last_name: selectedCommission.original_data?.last_name || selectedCommission.last_name || '',
      email: selectedCommission.original_data?.email || selectedCommission.email || '',
      phone: selectedCommission.original_data?.phone || selectedCommission.phone || '',
      reservation_number: selectedCommission.original_data?.reservation_number || selectedCommission.reservation_number || '',
      guest_first_name: selectedCommission.original_data?.guest_first_name || selectedCommission.guest_first_name || '',
      guest_last_name: selectedCommission.original_data?.guest_last_name || selectedCommission.guest_last_name || '',
      supplier: selectedCommission.original_data?.supplier || selectedCommission.supplier || '',
      booking_date: selectedCommission.original_data?.booking_date || selectedCommission.booking_date || '',
      travel_date: selectedCommission.original_data?.travel_date || selectedCommission.travel_date || '',
      gross_commission: selectedCommission.original_data?.gross_commission || selectedCommission.gross_commission || '',
      agent_commission: selectedCommission.original_data?.agent_commission || selectedCommission.agent_commission || '',
      notes: selectedCommission.original_data?.notes || selectedCommission.notes || ''
    });
    
    // Reset editing states
    setEditingFields({});
    setChangedFields([]);
  };

  const handleFieldChange = (fieldName, newValue) => {
    setEditFormData(prev => ({
      ...prev,
      [fieldName]: newValue
    }));
    
    // Track changed fields for the edit reason modal
    const originalValue = originalData[fieldName];
    const currentChangedFields = [...changedFields];
    
    if (newValue !== originalValue) {
      // Field has been changed
      if (!currentChangedFields.some(field => field.field === fieldName)) {
        currentChangedFields.push({
          field: fieldName,
          label: getFieldLabel(fieldName),
          originalValue: originalValue,
          newValue: newValue
        });
      } else {
        // Update existing changed field
        const index = currentChangedFields.findIndex(field => field.field === fieldName);
        currentChangedFields[index].newValue = newValue;
      }
    } else {
      // Field has been reverted to original value
      const index = currentChangedFields.findIndex(field => field.field === fieldName);
      if (index > -1) {
        currentChangedFields.splice(index, 1);
      }
    }
    
    setChangedFields(currentChangedFields);
  };
  
  const getFieldLabel = (fieldName) => {
    const labels = {
      first_name: 'First Name',
      last_name: 'Last Name',
      email: 'Email',
      phone: 'Phone',
      reservation_number: 'Reservation Number',
      guest_first_name: 'Guest First Name',
      guest_last_name: 'Guest Last Name',
      supplier: 'Supplier',
      booking_date: 'Booking Date',
      travel_date: 'Travel Date',
      gross_commission: 'Gross Commission',
      agent_commission: 'Agent Commission',
      notes: 'Notes'
    };
    return labels[fieldName] || fieldName;
  };
  
  const handleFieldEditToggle = (fieldName, isEditing) => {
    setEditingFields(prev => ({
      ...prev,
      [fieldName]: isEditing
    }));
  };
  
  const hasChanges = () => {
    return changedFields.length > 0;
  };

  const handleSaveAllChanges = () => {
    if (changedFields.length === 0) {
      alert('No changes have been made.');
      return;
    }
    setShowEditReasonModal(true);
  };
  
  const handleEditReasonSave = async (reason) => {
    setIsSaving(true);
    try {
      // Prepare only the changed fields for the API
      const changedData = {};
      changedFields.forEach(field => {
        changedData[field.field] = field.newValue;
      });
      
      await commissionsService.edit(selectedCommission.id, {
        ...changedData,
        editReason: reason
      });
      
      // Generate pre-filled message about the changes
      const fieldNames = changedFields.map(field => {
        const fieldName = field.field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        return `${fieldName}: ${field.originalValue} → ${field.newValue}`;
      }).join('\n');
      
      const prefilledMessage = `Hello,\n\nYour commission for reservation #${selectedCommission.reservation_number} has been updated by an administrator.\n\nChanges made:\n${fieldNames}\n\nReason for edit: ${reason}\n\nIf you have any questions about these changes, please don't hesitate to reach out.\n\nBest regards,\nAdmin Team`;
      
      // Reset edit states
      setIsEditMode(false);
      setEditFormData({});
      setOriginalData({});
      setEditingFields({});
      setChangedFields([]);
      setShowEditReasonModal(false);
      
      // Reload data to get updated commission
      await loadCommissions(pagination.page); // ✅ FIX: Preserve current page
      
      // Get the updated commission data
      const updatedCommissions = await commissionsService.getAll();
      const updatedCommission = updatedCommissions.find(c => c.id === selectedCommission.id);
      
      // Close detail modal and open messaging modal with pre-filled message
      setShowDetailModal(false);
      setSelectedCommissionForMessaging(updatedCommission || selectedCommission);
      setShowMessagingModal(true);
      
      // Store the pre-filled message for the messaging modal
      setPrefilledMessage(prefilledMessage);
      
    } catch (error) {
      console.error('Failed to update commission:', error);
      const serverMessage = error.response?.data?.error;
      const conflicts = error.response?.data?.existingCommissions;
      const conflictDetail = Array.isArray(conflicts) && conflicts.length > 0
        ? ` (conflicts with commission${conflicts.length > 1 ? 's' : ''} ${conflicts.map(c => `#${c.id} [${c.status}]`).join(', ')})`
        : '';
      alert(serverMessage
        ? `Failed to update commission: ${serverMessage}${conflictDetail}`
        : 'Failed to update commission. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditFormData({});
    setOriginalData({});
    setEditingFields({});
    setChangedFields([]);
    setShowEditReasonModal(false);
  };

  // Reload commissions when filters change - reset to page 1
  useEffect(() => {
    setPagination(prev => ({ ...prev, page: 1 }));
    loadCommissions(1);
  }, [filters.status, filters.agent, filters.dateRange]);

  // Debounced search effect - trigger search 500ms after user stops typing
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setPagination(prev => ({ ...prev, page: 1 }));
      loadCommissions(1); // Reset to page 1 when searching
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  // All filtering now handled server-side via API
  const filteredCommissions = commissions;

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'yellow';
      case 'approved': return 'green';
      case 'denied': return 'red';
      case 'on_hold': return 'orange';
      case 'needs_reply': return 'blue';
      case 'needs_admin_reply': return 'yellow';
      case 'needs_agent_reply': return 'blue';
      case 'paid': return 'purple';
      case 'processing': return 'indigo';
      default: return 'gray';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return Clock;
      case 'approved': return CheckCircle;
      case 'denied': return XCircle;
      case 'on_hold': return AlertCircle;
      case 'needs_reply': return MessageSquare;
      case 'needs_admin_reply': return MessageSquare;
      case 'needs_agent_reply': return MessageSquare;
      case 'paid': return DollarSign;
      case 'processing': return TrendingUp;
      default: return AlertCircle;
    }
  };

  return (
    <div style={{
      padding: isMobile ? travelieTheme.spacing.md : travelieTheme.spacing.xl,
      background: travelieTheme.colors.neutral.background,
      minHeight: '100vh'
    }}>
      {/* Header */}
      <div style={{ marginBottom: travelieTheme.spacing.xl }}>
        <h1 style={{
          fontSize: travelieTheme.typography.fontSize['3xl'],
          fontWeight: travelieTheme.typography.fontWeight.bold,
          color: travelieTheme.colors.text.primary,
          marginBottom: travelieTheme.spacing.sm
        }}>
          Commission Management
        </h1>
        <p style={{
          fontSize: travelieTheme.typography.fontSize.base,
          color: travelieTheme.colors.text.secondary
        }}>
          Review and process agent commission submissions
        </p>
      </div>

      {reviewBonusSettings && (
        <div style={{ background: '#FFFBEB', border: '1px solid #F59E0B', borderRadius: travelieTheme.components.card.borderRadius, padding: travelieTheme.spacing.lg, marginBottom: travelieTheme.spacing.xl }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: travelieTheme.spacing.md, flexWrap: 'wrap' }}>
            <div>
              <h2 style={{ margin: 0, color: '#92400E', fontSize: travelieTheme.typography.fontSize.lg }}>$5 MainStreet Review Bonus</h2>
              <p style={{ margin: '5px 0 0', color: '#78350F', fontSize: travelieTheme.typography.fontSize.sm }}>
                {reviewBonusClaims.filter(claim => claim.status === 'pending').length} proof submission(s) waiting for verification
              </p>
            </div>
            {['agency_admin', 'super_admin'].includes(user?.role) && (
              <button type="button" onClick={handleReviewBonusToggle} disabled={reviewBonusLoading} style={{ ...getButtonStyle(reviewBonusSettings.enabled ? 'danger' : 'success'), minWidth: '150px', justifyContent: 'center' }}>
                Promotion: {reviewBonusSettings.enabled ? 'On' : 'Off'}
              </button>
            )}
          </div>

          {reviewBonusClaims.filter(claim => claim.status === 'pending').length > 0 && (
            <div style={{ marginTop: travelieTheme.spacing.md, display: 'grid', gap: travelieTheme.spacing.sm }}>
              {reviewBonusClaims.filter(claim => claim.status === 'pending').map(claim => (
                <div key={claim.id} style={{ background: '#FFFFFF', border: '1px solid #FDE68A', borderRadius: '8px', padding: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                  <div>
                    <strong>{claim.agent_name}</strong> — review name: <strong>{claim.review_name}</strong>
                    <div style={{ color: '#6B7280', fontSize: '12px', marginTop: '3px' }}>
                      {claim.reservation_number ? `Commission ${claim.reservation_number}` : 'Waiting for the agent’s next commission submission'}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button type="button" onClick={() => viewReviewBonusScreenshot(claim.id)} style={getButtonStyle('info', 'small')}>View Screenshot</button>
                    <button type="button" onClick={() => handleReviewBonusDecision(claim, 'approved')} disabled={reviewBonusLoading} style={getButtonStyle('success', 'small')}>Approve $5</button>
                    <button type="button" onClick={() => handleReviewBonusDecision(claim, 'rejected')} disabled={reviewBonusLoading} style={getButtonStyle('danger', 'small')}>Reject</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Stats Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(3, 1fr)' : 'repeat(6, 1fr)',
        gap: travelieTheme.spacing.lg,
        marginBottom: travelieTheme.spacing.xl
      }}>
        <div style={getStatCardStyle()}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <p style={{
                fontSize: travelieTheme.typography.fontSize.sm,
                fontWeight: travelieTheme.typography.fontWeight.medium,
                color: travelieTheme.colors.text.secondary,
                marginBottom: travelieTheme.spacing.xs
              }}>Pending Review</p>
              <p style={{
                fontSize: travelieTheme.typography.fontSize['2xl'],
                fontWeight: travelieTheme.typography.fontWeight.bold,
                color: travelieTheme.colors.status.warning
              }}>{stats.pending}</p>
            </div>
            <Clock style={{ width: '32px', height: '32px', color: travelieTheme.colors.status.warning }} />
          </div>
        </div>
        <div style={getStatCardStyle()}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <p style={{
                fontSize: travelieTheme.typography.fontSize.sm,
                fontWeight: travelieTheme.typography.fontWeight.medium,
                color: travelieTheme.colors.text.secondary,
                marginBottom: travelieTheme.spacing.xs
              }}>Needs Reply</p>
              <p style={{
                fontSize: travelieTheme.typography.fontSize['2xl'],
                fontWeight: travelieTheme.typography.fontWeight.bold,
                color: travelieTheme.colors.primary.main
              }}>{stats.needsReply}</p>
            </div>
            <MessageSquare style={{ width: '32px', height: '32px', color: travelieTheme.colors.primary.main }} />
          </div>
        </div>
        <div style={getStatCardStyle()}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <p style={{
                fontSize: travelieTheme.typography.fontSize.sm,
                fontWeight: travelieTheme.typography.fontWeight.medium,
                color: travelieTheme.colors.text.secondary,
                marginBottom: travelieTheme.spacing.xs
              }}>Approved</p>
              <p style={{
                fontSize: travelieTheme.typography.fontSize['2xl'],
                fontWeight: travelieTheme.typography.fontWeight.bold,
                color: travelieTheme.colors.status.success
              }}>{stats.approved}</p>
            </div>
            <CheckCircle style={{ width: '32px', height: '32px', color: travelieTheme.colors.status.success }} />
          </div>
        </div>
        <div style={getStatCardStyle()}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <p style={{
                fontSize: travelieTheme.typography.fontSize.sm,
                fontWeight: travelieTheme.typography.fontWeight.medium,
                color: travelieTheme.colors.text.secondary,
                marginBottom: travelieTheme.spacing.xs
              }}>Processing</p>
              <p style={{
                fontSize: travelieTheme.typography.fontSize['2xl'],
                fontWeight: travelieTheme.typography.fontWeight.bold,
                color: '#6366F1'
              }}>{stats.processing}</p>
            </div>
            <TrendingUp style={{ width: '32px', height: '32px', color: '#6366F1' }} />
          </div>
        </div>
        <div style={getStatCardStyle()}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <p style={{
                fontSize: travelieTheme.typography.fontSize.sm,
                fontWeight: travelieTheme.typography.fontWeight.medium,
                color: travelieTheme.colors.text.secondary,
                marginBottom: travelieTheme.spacing.xs
              }}>Paid</p>
              <p style={{
                fontSize: travelieTheme.typography.fontSize['2xl'],
                fontWeight: travelieTheme.typography.fontWeight.bold,
                color: '#8B5CF6'
              }}>{stats.paid}</p>
            </div>
            <DollarSign style={{ width: '32px', height: '32px', color: '#8B5CF6' }} />
          </div>
        </div>
        <div style={getStatCardStyle()}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <p style={{
                fontSize: travelieTheme.typography.fontSize.sm,
                fontWeight: travelieTheme.typography.fontWeight.medium,
                color: travelieTheme.colors.text.secondary,
                marginBottom: travelieTheme.spacing.xs
              }}>On Hold</p>
              <p style={{
                fontSize: travelieTheme.typography.fontSize['2xl'],
                fontWeight: travelieTheme.typography.fontWeight.bold,
                color: '#F97316'
              }}>{stats.onHold}</p>
            </div>
            <AlertCircle style={{ width: '32px', height: '32px', color: '#F97316' }} />
          </div>
        </div>
        <div style={getStatCardStyle()}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <p style={{
                fontSize: travelieTheme.typography.fontSize.sm,
                fontWeight: travelieTheme.typography.fontWeight.medium,
                color: travelieTheme.colors.text.secondary,
                marginBottom: travelieTheme.spacing.xs
              }}>Denied</p>
              <p style={{
                fontSize: travelieTheme.typography.fontSize['2xl'],
                fontWeight: travelieTheme.typography.fontWeight.bold,
                color: travelieTheme.colors.status.error
              }}>{stats.denied}</p>
            </div>
            <XCircle style={{ width: '32px', height: '32px', color: travelieTheme.colors.status.error }} />
          </div>
        </div>
      </div>

      {/* Additional Stats Row */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
        gap: travelieTheme.spacing.lg,
        marginBottom: travelieTheme.spacing.xl
      }}>
        <div style={getStatCardStyle()}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <p style={{  
                fontSize: travelieTheme.typography.fontSize.sm,
                fontWeight: travelieTheme.typography.fontWeight.medium,
                color: travelieTheme.colors.text.secondary,
                marginBottom: travelieTheme.spacing.xs
              }}>Total Approved Amount</p>
              <p style={{
                fontSize: travelieTheme.typography.fontSize['2xl'],
                fontWeight: travelieTheme.typography.fontWeight.bold,
                color: travelieTheme.colors.text.primary
              }}>${stats.totalAmount.toLocaleString()}</p>
            </div>
            <DollarSign style={{ width: '32px', height: '32px', color: travelieTheme.colors.status.success }} />
          </div>
        </div>
        <div style={getStatCardStyle()}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <p style={{
                fontSize: travelieTheme.typography.fontSize.sm,
                fontWeight: travelieTheme.typography.fontWeight.medium,
                color: travelieTheme.colors.text.secondary,
                marginBottom: travelieTheme.spacing.xs
              }}>Total Paid Amount</p>
              <p style={{
                fontSize: travelieTheme.typography.fontSize['2xl'],
                fontWeight: travelieTheme.typography.fontWeight.bold,
                color: travelieTheme.colors.text.primary
              }}>${stats.paidAmount.toLocaleString()}</p>
            </div>
            <DollarSign style={{ width: '32px', height: '32px', color: '#8B5CF6' }} />
          </div>
        </div>
        <div style={getStatCardStyle()}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <p style={{
                fontSize: travelieTheme.typography.fontSize.sm,
                fontWeight: travelieTheme.typography.fontWeight.medium,
                color: travelieTheme.colors.text.secondary,
                marginBottom: travelieTheme.spacing.xs
              }}>Average Commission</p>
              <p style={{
                fontSize: travelieTheme.typography.fontSize['2xl'],
                fontWeight: travelieTheme.typography.fontWeight.bold,
                color: travelieTheme.colors.text.primary
              }}>${stats.avgCommission.toLocaleString()}</p>
            </div>
            <TrendingUp style={{ width: '32px', height: '32px', color: travelieTheme.colors.primary.main }} />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div style={{
        background: '#FFFFFF',
        borderRadius: travelieTheme.components.card.borderRadius,
        boxShadow: travelieTheme.components.card.shadow,
        marginBottom: travelieTheme.spacing.lg
      }}>
        <div style={{
          padding: travelieTheme.spacing.md,
          borderBottom: `1px solid ${travelieTheme.colors.neutral.border}`
        }}>
          <div style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: travelieTheme.spacing.md
          }}>
            <div style={{ flex: 1, position: 'relative' }}>
              <Search style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: travelieTheme.colors.text.secondary,
                width: '20px',
                height: '20px'
              }} />
              <input
                type="text"
                placeholder="Search by agent name, guest name, or reservation..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  ...inputStyle,
                  paddingLeft: '40px',
                  minWidth: '300px'
                }}
                onFocus={(e) => e.target.style.borderColor = travelieTheme.colors.primary.main}
                onBlur={(e) => e.target.style.borderColor = travelieTheme.colors.neutral.border}
              />
            </div>
            <select
              value={filters.agent}
              onChange={(e) => setFilters({ ...filters, agent: e.target.value })}
              style={inputStyle}
              onFocus={(e) => e.target.style.borderColor = travelieTheme.colors.primary.main}
              onBlur={(e) => e.target.style.borderColor = travelieTheme.colors.neutral.border}
            >
              <option value="all">All Agents</option>
              {agents.map(agent => (
                <option key={agent.id} value={agent.id}>
                  {agent.name || `${agent.first_name} ${agent.last_name}`}
                </option>
              ))}
            </select>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              style={inputStyle}
              onFocus={(e) => e.target.style.borderColor = travelieTheme.colors.primary.main}
              onBlur={(e) => e.target.style.borderColor = travelieTheme.colors.neutral.border}
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="processing">Processing</option>
              <option value="denied">Denied</option>
              <option value="on_hold">On Hold</option>
              <option value="needs_reply">Needs Reply</option>
              <option value="needs_admin_reply">Needs Admin Reply</option>
              <option value="needs_agent_reply">Needs Agent Reply</option>
              <option value="paid">Paid</option>
            </select>
            <select
              value={filters.dateRange}
              onChange={(e) => setFilters({ ...filters, dateRange: e.target.value })}
              style={inputStyle}
              onFocus={(e) => e.target.style.borderColor = travelieTheme.colors.primary.main}
              onBlur={(e) => e.target.style.borderColor = travelieTheme.colors.neutral.border}
            >
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="week">Last 7 Days</option>
              <option value="month">Last 30 Days</option>
            </select>
            <button
              onClick={() => setShowUploadModal(true)}
              style={getButtonStyle('primary', 'medium')}
              onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
              onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
            >
              <Upload style={{ width: '16px', height: '16px' }} />
              <span>Upload CSV</span>
            </button>
            <button
              onClick={() => setShowExportModal(true)}
              style={getButtonStyle('purple', 'medium')}
              onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
              onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
            >
              <Download style={{ width: '16px', height: '16px' }} />
              <span>Export</span>
            </button>
            <button
              onClick={handleOpenPayConfirmation}
              style={getButtonStyle('success', 'medium')}
              onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
              onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
            >
              <DollarSign style={{ width: '16px', height: '16px' }} />
              <span>Pay</span>
            </button>
          </div>
        </div>
      </div>

      {/* Bulk Actions */}
      {showBulkActions && (
        <div style={{
          background: travelieTheme.colors.primary.light,
          border: `1px solid ${travelieTheme.colors.primary.main}`,
          borderRadius: travelieTheme.components.card.borderRadius,
          padding: travelieTheme.spacing.md,
          marginBottom: travelieTheme.spacing.lg
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexDirection: isMobile ? 'column' : 'row',
            gap: travelieTheme.spacing.md
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: travelieTheme.spacing.md }}>
              <span style={{
                fontSize: travelieTheme.typography.fontSize.sm,
                fontWeight: travelieTheme.typography.fontWeight.medium,
                color: travelieTheme.colors.primary.main
              }}>
                {selectedCommissions.length} commission(s) selected
              </span>
              <button
                onClick={() => {
                  setSelectedCommissions([]);
                  setSelectAll(false);
                  setShowBulkActions(false);
                }}
                style={{
                  fontSize: travelieTheme.typography.fontSize.sm,
                  color: travelieTheme.colors.primary.main,
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  textDecoration: 'underline'
                }}
                onMouseEnter={(e) => e.currentTarget.style.color = travelieTheme.colors.primary.hover}
                onMouseLeave={(e) => e.currentTarget.style.color = travelieTheme.colors.primary.main}
              >
                Clear selection
              </button>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: travelieTheme.spacing.sm, flexWrap: 'wrap' }}>
              <span style={{
                fontSize: travelieTheme.typography.fontSize.sm,
                fontWeight: travelieTheme.typography.fontWeight.medium,
                color: travelieTheme.colors.primary.main
              }}>Bulk Actions:</span>
              <button
                onClick={() => handleBulkStatusUpdate('approved')}
                style={getButtonStyle('success', 'small')}
                onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
                onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
              >
                Approve All
              </button>
              <button
                onClick={() => handleBulkStatusUpdate('processing')}
                style={{ ...getButtonStyle('info', 'small'), background: '#6366F1' }}
                onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
                onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
              >
                Mark Processing
              </button>
              <button
                onClick={() => handleBulkStatusUpdate('denied')}
                style={getButtonStyle('danger', 'small')}
                onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
                onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
              >
                Deny All
              </button>
              <button
                onClick={() => handleBulkStatusUpdate('on_hold')}
                style={getButtonStyle('warning', 'small')}
                onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
                onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
              >
                Hold All
              </button>
              <button
                onClick={() => handleBulkStatusUpdate('paid')}
                style={getButtonStyle('purple', 'small')}
                onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
                onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
              >
                Mark All Paid
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Commissions Table */}
      <div style={{
        background: '#FFFFFF',
        borderRadius: travelieTheme.components.card.borderRadius,
        boxShadow: travelieTheme.components.card.shadow
      }}>
        <div style={{ padding: travelieTheme.spacing.lg }}>
          {commissions.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: `${travelieTheme.spacing.xl} 0`
            }}>
              <p style={{
                fontSize: travelieTheme.typography.fontSize.base,
                color: travelieTheme.colors.text.secondary
              }}>No commissions found</p>
            </div>
          ) : (
            <div style={{ width: '100%', overflowX: 'hidden' }}>
              {/* Desktop Table View */}
              <div style={{ display: isDesktopOrWide ? 'block' : 'none' }}>
                <table style={{ width: '100%', tableLayout: 'fixed' }}>
              <thead>
                <tr style={{ background: travelieTheme.colors.neutral.background }}>
                  <th style={{
                    padding: `${travelieTheme.spacing.sm} ${travelieTheme.spacing.lg}`,
                    textAlign: 'left',
                    width: '60px'
                  }}>
                    <input
                      type="checkbox"
                      checked={selectAll}
                      onChange={handleSelectAll}
                      style={{
                        width: '16px',
                        height: '16px',
                        cursor: 'pointer',
                        accentColor: travelieTheme.colors.primary.main
                      }}
                    />
                  </th>
                  <th style={{
                    padding: `${travelieTheme.spacing.sm} ${travelieTheme.spacing.xs}`,
                    textAlign: 'left',
                    fontSize: travelieTheme.typography.fontSize.xs,
                    fontWeight: travelieTheme.typography.fontWeight.medium,
                    color: travelieTheme.colors.text.secondary,
                    textTransform: 'uppercase',
                    width: '18%'
                  }}>Agent</th>
                  <th style={{
                    padding: `${travelieTheme.spacing.sm} ${travelieTheme.spacing.xs}`,
                    textAlign: 'left',
                    fontSize: travelieTheme.typography.fontSize.xs,
                    fontWeight: travelieTheme.typography.fontWeight.medium,
                    color: travelieTheme.colors.text.secondary,
                    textTransform: 'uppercase',
                    width: '14%'
                  }}>Guest</th>
                  <th style={{
                    padding: `${travelieTheme.spacing.sm} ${travelieTheme.spacing.xs}`,
                    textAlign: 'left',
                    fontSize: travelieTheme.typography.fontSize.xs,
                    fontWeight: travelieTheme.typography.fontWeight.medium,
                    color: travelieTheme.colors.text.secondary,
                    textTransform: 'uppercase',
                    width: '16%'
                  }}>Booking</th>
                  <th style={{
                    padding: `${travelieTheme.spacing.sm} ${travelieTheme.spacing.xs}`,
                    textAlign: 'left',
                    fontSize: travelieTheme.typography.fontSize.xs,
                    fontWeight: travelieTheme.typography.fontWeight.medium,
                    color: travelieTheme.colors.text.secondary,
                    textTransform: 'uppercase',
                    width: '14%'
                  }}>Commission</th>
                  <th style={{
                    padding: `${travelieTheme.spacing.sm} ${travelieTheme.spacing.xs}`,
                    textAlign: 'left',
                    fontSize: travelieTheme.typography.fontSize.xs,
                    fontWeight: travelieTheme.typography.fontWeight.medium,
                    color: travelieTheme.colors.text.secondary,
                    textTransform: 'uppercase',
                    width: '12%'
                  }}>Status</th>
                  <th style={{
                    padding: `${travelieTheme.spacing.sm} ${travelieTheme.spacing.xs}`,
                    textAlign: 'left',
                    fontSize: travelieTheme.typography.fontSize.xs,
                    fontWeight: travelieTheme.typography.fontWeight.medium,
                    color: travelieTheme.colors.text.secondary,
                    textTransform: 'uppercase',
                    width: '12%'
                  }}>Submitted</th>
                  <th style={{
                    padding: `${travelieTheme.spacing.sm} ${travelieTheme.spacing.xs}`,
                    textAlign: 'left',
                    fontSize: travelieTheme.typography.fontSize.xs,
                    fontWeight: travelieTheme.typography.fontWeight.medium,
                    color: travelieTheme.colors.text.secondary,
                    textTransform: 'uppercase',
                    width: '14%'
                  }}>Actions</th>
                </tr>
              </thead>
              <tbody style={{ background: '#FFFFFF' }}>
                {commissions.map((commission) => {
                  const StatusIcon = getStatusIcon(commission.status);
                  const statusColor = getStatusColor(commission.status);
                  
                  return (
                    <tr 
                      key={commission.id}
                      style={{
                        borderBottom: `1px solid ${travelieTheme.colors.neutral.border}`,
                        ...(commission.is_duplicate_flag === 1 && {
                          background: '#FEF2F2',
                          borderLeft: '4px solid #EF4444'
                        })
                      }}
                      onMouseEnter={(e) => commission.is_duplicate_flag !== 1 && (e.currentTarget.style.background = travelieTheme.colors.neutral.hover)}
                      onMouseLeave={(e) => commission.is_duplicate_flag !== 1 && (e.currentTarget.style.background = '#FFFFFF')}
                    >
                      <td style={{ padding: `${travelieTheme.spacing.md} ${travelieTheme.spacing.lg}`, width: '60px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: travelieTheme.spacing.sm }}>
                          <input
                            type="checkbox"
                            checked={selectedCommissions.includes(commission.id)}
                            onChange={() => handleSelectCommission(commission.id)}
                            style={{
                              width: '16px',
                              height: '16px',
                              cursor: 'pointer',
                              accentColor: travelieTheme.colors.primary.main
                            }}
                          />
                          {commission.is_duplicate_flag === 1 && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <span style={{ color: '#DC2626', fontSize: '18px' }} title="Duplicate reservation number detected (contains match)">
                                🚩
                              </span>
                              <span style={{
                                fontSize: travelieTheme.typography.fontSize.xs,
                                background: '#FEE2E2',
                                color: '#991B1B',
                                padding: `${travelieTheme.spacing.xs} ${travelieTheme.spacing.sm}`,
                                borderRadius: '9999px',
                                fontWeight: travelieTheme.typography.fontWeight.medium
                              }}>
                                DUPLICATE
                              </span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td style={{ padding: `${travelieTheme.spacing.md} ${travelieTheme.spacing.xs}`, width: '18%' }}>
                        <div>
                          <p style={{
                            fontSize: travelieTheme.typography.fontSize.sm,
                            fontWeight: travelieTheme.typography.fontWeight.medium,
                            color: travelieTheme.colors.text.primary,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {commission.first_name} {commission.last_name}
                          </p>
                          <p style={{
                            fontSize: travelieTheme.typography.fontSize.xs,
                            color: travelieTheme.colors.text.secondary,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>{commission.email}</p>
                        </div>
                      </td>
                      <td style={{ padding: `${travelieTheme.spacing.md} ${travelieTheme.spacing.xs}`, width: '14%' }}>
                        <p style={{
                          fontSize: travelieTheme.typography.fontSize.sm,
                          color: travelieTheme.colors.text.primary,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {commission.guest_first_name} {commission.guest_last_name}
                        </p>
                      </td>
                      <td style={{ padding: `${travelieTheme.spacing.md} ${travelieTheme.spacing.xs}`, width: '16%' }}>
                        <div style={{ fontSize: travelieTheme.typography.fontSize.sm }}>
                          <p style={{
                            color: travelieTheme.colors.text.primary,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>{commission.reservation_number}</p>
                          <p style={{
                            color: travelieTheme.colors.text.secondary,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>{commission.supplier}</p>
                        </div>
                      </td>
                      <td style={{ padding: `${travelieTheme.spacing.md} ${travelieTheme.spacing.xs}`, width: '14%' }}>
                        <div style={{ fontSize: travelieTheme.typography.fontSize.sm }}>
                          <p style={{
                            fontWeight: travelieTheme.typography.fontWeight.medium,
                            color: travelieTheme.colors.text.primary,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            ${parseFloat(commission.agent_commission || 0).toFixed(2)}
                          </p>
                          <p style={{
                            fontSize: travelieTheme.typography.fontSize.xs,
                            color: travelieTheme.colors.text.secondary,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            Gross: ${parseFloat(commission.gross_commission || 0).toFixed(2)}
                          </p>
                        </div>
                      </td>
                      <td style={{ padding: `${travelieTheme.spacing.md} ${travelieTheme.spacing.xs}`, width: '12%' }}>
                        <span style={{
                          ...travelieTheme.getStatusBadgeStyle(commission.status),
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          maxWidth: '100%'
                        }}>
                          <StatusIcon style={{ width: '12px', height: '12px', flexShrink: 0 }} />
                          <span style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>{commission.status}</span>
                        </span>
                      </td>
                      <td style={{
                        padding: `${travelieTheme.spacing.md} ${travelieTheme.spacing.xs}`,
                        fontSize: travelieTheme.typography.fontSize.sm,
                        color: travelieTheme.colors.text.secondary,
                        width: '12%'
                      }}>
                        <span style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          display: 'block'
                        }}>{new Date(commission.created_at).toLocaleDateString()}</span>
                      </td>
                      <td style={{ padding: `${travelieTheme.spacing.md} ${travelieTheme.spacing.xs}`, width: '14%' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: travelieTheme.spacing.xs }}>
                          <button
                            onClick={async () => {
                              try {
                                // Fetch individual commission data to ensure we have original_data
                                const fullCommissionData = await commissionsService.getById(commission.id);
                                console.log('Full commission data received:', fullCommissionData);
                                console.log('Original data field:', fullCommissionData.original_data);
                                
                                // Parse original_data if it's a string - handle multiple levels of escaping
                                if (fullCommissionData.original_data && typeof fullCommissionData.original_data === 'string') {
                                  try {
                                    let parsedData = fullCommissionData.original_data;
                                    // Keep parsing until we get an object or can't parse anymore
                                    let attempts = 0;
                                    while (typeof parsedData === 'string' && attempts < 10) {
                                      try {
                                        parsedData = JSON.parse(parsedData);
                                        attempts++;
                                      } catch (e) {
                                        break;
                                      }
                                    }
                                    if (typeof parsedData === 'object' && parsedData !== null) {
                                      fullCommissionData.original_data = parsedData;
                                      console.log('Successfully parsed original_data:', fullCommissionData.original_data);
                                    } else {
                                      console.warn('Could not parse original_data to object:', parsedData);
                                    }
                                  } catch (parseError) {
                                    console.error('Error parsing original_data:', parseError);
                                  }
                                }
                                
                                setSelectedCommission(fullCommissionData);
                                setShowDetailModal(true);
                              } catch (error) {
                                console.error('Error fetching commission details:', error);
                                // Fallback to existing data if fetch fails
                                setSelectedCommission(commission);
                                setShowDetailModal(true);
                              }
                            }}
                            style={getButtonStyle('info', 'small')}
                            onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-1px)'}
                            onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                          >
                            Review
                          </button>
                          <button
                            onClick={() => {
                              setSelectedCommissionForMessaging(commission);
                              setShowMessagingModal(true);
                            }}
                            style={commission.needs_admin_reply 
                              ? {
                                  ...getButtonStyle('danger', 'small'),
                                  background: '#FEE2E2',
                                  color: '#991B1B',
                                  border: '1px solid #FCA5A5'
                                }
                              : getButtonStyle('info', 'small')
                            }
                            onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-1px)'}
                            onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                          >
                            {commission.needs_admin_reply ? '🔴 Message' : 'Message'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
                </table>
              </div>
              
              {/* Mobile Card View */}
              <div style={{ display: isDesktopOrWide ? 'none' : 'block' }}>
                {commissions.map((commission) => {
                  const StatusIcon = getStatusIcon(commission.status);
                  const statusColor = getStatusColor(commission.status);
                  
                  return (
                    <div key={commission.id} style={{
                      background: commission.is_duplicate_flag === 1 ? '#FEF2F2' : '#FFFFFF',
                      border: `1px solid ${travelieTheme.colors.neutral.border}`,
                      borderRadius: travelieTheme.components.card.borderRadius,
                      padding: travelieTheme.spacing.md,
                      boxShadow: travelieTheme.components.card.shadow,
                      marginBottom: travelieTheme.spacing.md,
                      ...(commission.is_duplicate_flag === 1 && {
                        borderLeft: '4px solid #EF4444'
                      })
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: travelieTheme.spacing.sm }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: travelieTheme.spacing.sm }}>
                          <input
                            type="checkbox"
                            checked={selectedCommissions.includes(commission.id)}
                            onChange={() => handleSelectCommission(commission.id)}
                            style={{
                              width: '16px',
                              height: '16px',
                              cursor: 'pointer',
                              accentColor: travelieTheme.colors.primary.main
                            }}
                          />
                          {commission.is_duplicate_flag === 1 && (
                            <span style={{ color: '#DC2626', fontSize: '18px' }} title="Duplicate reservation number detected">
                              🚩
                            </span>
                          )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: travelieTheme.spacing.sm }}>
                          <span style={{
                            ...travelieTheme.getStatusBadgeStyle(commission.status),
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}>
                            <StatusIcon style={{ width: '12px', height: '12px' }} />
                            {commission.status}
                          </span>
                        </div>
                      </div>
                      
                      <div style={{ marginBottom: travelieTheme.spacing.sm }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: travelieTheme.spacing.xs }}>
                          <span style={{
                            fontSize: travelieTheme.typography.fontSize.sm,
                            fontWeight: travelieTheme.typography.fontWeight.medium,
                            color: travelieTheme.colors.text.primary
                          }}>{commission.first_name} {commission.last_name}</span>
                          <span style={{
                            fontSize: travelieTheme.typography.fontSize.sm,
                            fontWeight: travelieTheme.typography.fontWeight.semibold,
                            color: travelieTheme.colors.status.success
                          }}>${parseFloat(commission.agent_commission || 0).toFixed(2)}</span>
                        </div>
                        
                        <div style={{ fontSize: travelieTheme.typography.fontSize.sm, color: travelieTheme.colors.text.secondary }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: travelieTheme.spacing.xs }}>
                            <span>Guest: {commission.guest_first_name} {commission.guest_last_name}</span>
                            <span style={{ fontSize: travelieTheme.typography.fontSize.xs, color: travelieTheme.colors.text.secondary }}>
                              {new Date(commission.created_at).toLocaleDateString()}
                            </span>
                          </div>
                          <div style={{ marginTop: travelieTheme.spacing.xs }}>
                            <span style={{
                              fontSize: travelieTheme.typography.fontSize.xs,
                              background: travelieTheme.colors.neutral.background,
                              padding: `${travelieTheme.spacing.xs} ${travelieTheme.spacing.sm}`,
                              borderRadius: '4px'
                            }}>
                              {commission.reservation_number}
                            </span>
                          </div>
                        </div>
                        
                        {commission.supplier && (
                          <div style={{
                            fontSize: travelieTheme.typography.fontSize.xs,
                            color: travelieTheme.colors.text.secondary,
                            marginTop: travelieTheme.spacing.xs,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {commission.supplier}
                          </div>
                        )}
                      </div>
                      
                      <div style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: travelieTheme.spacing.sm,
                        paddingTop: travelieTheme.spacing.sm,
                        borderTop: `1px solid ${travelieTheme.colors.neutral.border}`
                      }}>
                        <button
                          onClick={async () => {
                            try {
                              const fullCommissionData = await commissionsService.getById(commission.id);
                              if (fullCommissionData.original_data && typeof fullCommissionData.original_data === 'string') {
                                try {
                                  let parsedData = fullCommissionData.original_data;
                                  let attempts = 0;
                                  while (typeof parsedData === 'string' && attempts < 10) {
                                    try {
                                      parsedData = JSON.parse(parsedData);
                                      attempts++;
                                    } catch (e) {
                                      break;
                                    }
                                  }
                                  if (typeof parsedData === 'object' && parsedData !== null) {
                                    fullCommissionData.original_data = parsedData;
                                  }
                                } catch (parseError) {
                                  console.error('Error parsing original_data:', parseError);
                                }
                              }
                              setSelectedCommission(fullCommissionData);
                              setShowDetailModal(true);
                            } catch (error) {
                              console.error('Error fetching commission details:', error);
                              setSelectedCommission(commission);
                              setShowDetailModal(true);
                            }
                          }}
                          style={{
                            padding: travelieTheme.spacing.sm,
                            color: travelieTheme.colors.primary.main,
                            background: 'transparent',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = travelieTheme.colors.primary.light}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                          title="View Details"
                        >
                          <Eye style={{ width: '16px', height: '16px' }} />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedCommissionForMessaging(commission);
                            setShowMessagingModal(true);
                          }}
                          style={{
                            padding: travelieTheme.spacing.sm,
                            color: travelieTheme.colors.status.success,
                            background: 'transparent',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = travelieTheme.colors.status.successLight}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                          title="Message Agent"
                        >
                          <MessageSquare style={{ width: '16px', height: '16px' }} />
                        </button>
                        <button
                          onClick={() => setSelectedCommission(commission)}
                          style={{
                            padding: travelieTheme.spacing.sm,
                            color: travelieTheme.colors.text.secondary,
                            background: 'transparent',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = travelieTheme.colors.neutral.hover}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                          title="More Actions"
                        >
                          <MoreHorizontal className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          
          {pagination.totalPages > 1 && (
            <>
            {/* Pagination - Now outside table container for better visibility */}
            <div style={{
              background: '#FFFFFF',
              padding: `${travelieTheme.spacing.sm} ${travelieTheme.spacing.md}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderTop: `1px solid ${travelieTheme.colors.neutral.border}`
            }}>
              <div style={{ flex: 1, display: isMobile ? 'flex' : 'none', justifyContent: 'space-between' }}>
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  style={{
                    ...getButtonStyle('secondary', 'small'),
                    opacity: pagination.page === 1 ? '0.5' : '1',
                    cursor: pagination.page === 1 ? 'not-allowed' : 'pointer'
                  }}
                >
                  Previous
                </button>
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.totalPages}
                  style={{
                    ...getButtonStyle('secondary', 'small'),
                    marginLeft: travelieTheme.spacing.sm,
                    opacity: pagination.page === pagination.totalPages ? '0.5' : '1',
                    cursor: pagination.page === pagination.totalPages ? 'not-allowed' : 'pointer'
                  }}
                >
                  Next
                </button>
              </div>
              <div style={{ flex: 1, display: isMobile ? 'none' : 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <p style={{ fontSize: travelieTheme.typography.fontSize.sm, color: travelieTheme.colors.text.secondary }}>
                    Showing{' '}
                    <span style={{ fontWeight: travelieTheme.typography.fontWeight.medium }}>{((pagination.page - 1) * pagination.limit) + 1}</span>
                    {' '}to{' '}
                    <span style={{ fontWeight: travelieTheme.typography.fontWeight.medium }}>
                      {Math.min(pagination.page * pagination.limit, pagination.total)}
                    </span>
                    {' '}of{' '}
                    <span style={{ fontWeight: travelieTheme.typography.fontWeight.medium }}>{pagination.total}</span>
                    {' '}results
                  </p>
                </div>
                <div>
                  <nav style={{ position: 'relative', zIndex: 0, display: 'inline-flex', borderRadius: '6px', boxShadow: travelieTheme.components.card.shadow }} aria-label="Pagination">
                    {/* Previous button */}
                    <button
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={pagination.page === 1}
                      style={{
                        position: 'relative',
                        display: 'inline-flex',
                        alignItems: 'center',
                        padding: `${travelieTheme.spacing.sm} ${travelieTheme.spacing.sm}`,
                        borderTopLeftRadius: '6px',
                        borderBottomLeftRadius: '6px',
                        border: `1px solid ${travelieTheme.colors.neutral.border}`,
                        background: '#FFFFFF',
                        fontSize: travelieTheme.typography.fontSize.sm,
                        fontWeight: travelieTheme.typography.fontWeight.medium,
                        color: travelieTheme.colors.text.secondary,
                        cursor: pagination.page === 1 ? 'not-allowed' : 'pointer',
                        opacity: pagination.page === 1 ? '0.5' : '1'
                      }}
                      onMouseEnter={(e) => pagination.page !== 1 && (e.currentTarget.style.background = travelieTheme.colors.neutral.hover)}
                      onMouseLeave={(e) => pagination.page !== 1 && (e.currentTarget.style.background = '#FFFFFF')}
                    >
                      <span style={{ position: 'absolute', width: '1px', height: '1px', padding: 0, margin: '-1px', overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap', borderWidth: 0 }}>Previous</span>
                      <svg style={{ height: '20px', width: '20px' }} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </button>
                    
                    {/* Page numbers */}
                    {(() => {
                      const pages = [];
                      const startPage = Math.max(1, pagination.page - 2);
                      const endPage = Math.min(pagination.totalPages, pagination.page + 2);
                      
                      // First page
                      if (startPage > 1) {
                        pages.push(
                          <button
                            key={1}
                            onClick={() => handlePageChange(1)}
                            style={{
                              position: 'relative',
                              display: 'inline-flex',
                              alignItems: 'center',
                              padding: `${travelieTheme.spacing.sm} ${travelieTheme.spacing.md}`,
                              border: `1px solid ${travelieTheme.colors.neutral.border}`,
                              borderLeft: 'none',
                              background: '#FFFFFF',
                              fontSize: travelieTheme.typography.fontSize.sm,
                              fontWeight: travelieTheme.typography.fontWeight.medium,
                              color: travelieTheme.colors.text.primary,
                              cursor: 'pointer'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = travelieTheme.colors.neutral.hover}
                            onMouseLeave={(e) => e.currentTarget.style.background = '#FFFFFF'}
                          >
                            1
                          </button>
                        );
                        if (startPage > 2) {
                          pages.push(
                            <span key="ellipsis1" style={{
                              position: 'relative',
                              display: 'inline-flex',
                              alignItems: 'center',
                              padding: `${travelieTheme.spacing.sm} ${travelieTheme.spacing.md}`,
                              border: `1px solid ${travelieTheme.colors.neutral.border}`,
                              borderLeft: 'none',
                              background: '#FFFFFF',
                              fontSize: travelieTheme.typography.fontSize.sm,
                              fontWeight: travelieTheme.typography.fontWeight.medium,
                              color: travelieTheme.colors.text.primary
                            }}>
                              ...
                            </span>
                          );
                        }
                      }
                      
                      // Current page range
                      for (let i = startPage; i <= endPage; i++) {
                        pages.push(
                          <button
                            key={i}
                            onClick={() => handlePageChange(i)}
                            style={{
                              position: 'relative',
                              display: 'inline-flex',
                              alignItems: 'center',
                              padding: `${travelieTheme.spacing.sm} ${travelieTheme.spacing.md}`,
                              border: `1px solid ${i === pagination.page ? travelieTheme.colors.primary.main : travelieTheme.colors.neutral.border}`,
                              borderLeft: 'none',
                              background: i === pagination.page ? travelieTheme.colors.primary.light : '#FFFFFF',
                              fontSize: travelieTheme.typography.fontSize.sm,
                              fontWeight: travelieTheme.typography.fontWeight.medium,
                              color: i === pagination.page ? travelieTheme.colors.primary.main : travelieTheme.colors.text.primary,
                              cursor: 'pointer',
                              zIndex: i === pagination.page ? 10 : 0
                            }}
                            onMouseEnter={(e) => i !== pagination.page && (e.currentTarget.style.background = travelieTheme.colors.neutral.hover)}
                            onMouseLeave={(e) => i !== pagination.page && (e.currentTarget.style.background = '#FFFFFF')}
                          >
                            {i}
                          </button>
                        );
                      }
                      
                      // Last page
                      if (endPage < pagination.totalPages) {
                        if (endPage < pagination.totalPages - 1) {
                          pages.push(
                            <span key="ellipsis2" style={{
                              position: 'relative',
                              display: 'inline-flex',
                              alignItems: 'center',
                              padding: `${travelieTheme.spacing.sm} ${travelieTheme.spacing.md}`,
                              border: `1px solid ${travelieTheme.colors.neutral.border}`,
                              borderLeft: 'none',
                              background: '#FFFFFF',
                              fontSize: travelieTheme.typography.fontSize.sm,
                              fontWeight: travelieTheme.typography.fontWeight.medium,
                              color: travelieTheme.colors.text.primary
                            }}>
                              ...
                            </span>
                          );
                        }
                        pages.push(
                          <button
                            key={pagination.totalPages}
                            onClick={() => handlePageChange(pagination.totalPages)}
                            style={{
                              position: 'relative',
                              display: 'inline-flex',
                              alignItems: 'center',
                              padding: `${travelieTheme.spacing.sm} ${travelieTheme.spacing.md}`,
                              border: `1px solid ${travelieTheme.colors.neutral.border}`,
                              borderLeft: 'none',
                              background: '#FFFFFF',
                              fontSize: travelieTheme.typography.fontSize.sm,
                              fontWeight: travelieTheme.typography.fontWeight.medium,
                              color: travelieTheme.colors.text.primary,
                              cursor: 'pointer'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = travelieTheme.colors.neutral.hover}
                            onMouseLeave={(e) => e.currentTarget.style.background = '#FFFFFF'}
                          >
                            {pagination.totalPages}
                          </button>
                        );
                      }
                      
                      return pages;
                    })()}
                    
                    {/* Next button */}
                    <button
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={pagination.page === pagination.totalPages}
                      style={{
                        position: 'relative',
                        display: 'inline-flex',
                        alignItems: 'center',
                        padding: `${travelieTheme.spacing.sm} ${travelieTheme.spacing.sm}`,
                        borderTopRightRadius: '6px',
                        borderBottomRightRadius: '6px',
                        border: `1px solid ${travelieTheme.colors.neutral.border}`,
                        borderLeft: 'none',
                        background: '#FFFFFF',
                        fontSize: travelieTheme.typography.fontSize.sm,
                        fontWeight: travelieTheme.typography.fontWeight.medium,
                        color: travelieTheme.colors.text.secondary,
                        cursor: pagination.page === pagination.totalPages ? 'not-allowed' : 'pointer',
                        opacity: pagination.page === pagination.totalPages ? '0.5' : '1'
                      }}
                      onMouseEnter={(e) => pagination.page !== pagination.totalPages && (e.currentTarget.style.background = travelieTheme.colors.neutral.hover)}
                      onMouseLeave={(e) => pagination.page !== pagination.totalPages && (e.currentTarget.style.background = '#FFFFFF')}
                    >
                      <span style={{ position: 'absolute', width: '1px', height: '1px', padding: 0, margin: '-1px', overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap', borderWidth: 0 }}>Next</span>
                      <svg style={{ height: '20px', width: '20px' }} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </nav>
                </div>
              </div>
            </div>
            </>
          )}
          
          {/* Show total count when no pagination needed */}
          {pagination.totalPages <= 1 && pagination.total > 0 && (
            <div style={{
              textAlign: 'center',
              padding: `${travelieTheme.spacing.sm} 0`,
              fontSize: travelieTheme.typography.fontSize.sm,
              color: travelieTheme.colors.text.secondary,
              borderTop: `1px solid ${travelieTheme.colors.neutral.border}`
            }}>
              Showing {commissions.length} of {pagination.total} commissions
            </div>
          )}
        </div>
      </div>

      {/* Commission Detail Modal */}
      {showDetailModal && selectedCommission && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 50,
          padding: travelieTheme.spacing.md
        }}>
          <div style={{
            background: '#FFFFFF',
            borderRadius: travelieTheme.components.card.borderRadius,
            boxShadow: travelieTheme.components.card.shadow,
            maxWidth: '896px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <div style={{
              padding: travelieTheme.spacing.lg,
              borderBottom: `1px solid ${travelieTheme.colors.neutral.border}`
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2 style={{
                  fontSize: travelieTheme.typography.fontSize.xl,
                  fontWeight: travelieTheme.typography.fontWeight.semibold,
                  color: travelieTheme.colors.text.primary
                }}>
                  {isEditMode ? 'Edit Commission' : 'Commission Details'}
                </h2>
                <div style={{ display: 'flex', alignItems: 'center', gap: travelieTheme.spacing.sm }}>
                  {!isEditMode && (
                    <button
                      onClick={handleEditCommission}
                      style={{
                        ...getButtonStyle('primary', 'small'),
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      <FileText style={{ width: '16px', height: '16px' }} />
                      Edit
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setShowDetailModal(false);
                      setIsEditMode(false);
                      setEditFormData({});
                      setEditReason('');
                    }}
                    style={{
                      color: travelieTheme.colors.text.secondary,
                      background: 'transparent',
                      border: 'none',
                      padding: travelieTheme.spacing.xs,
                      cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = travelieTheme.colors.text.primary}
                    onMouseLeave={(e) => e.currentTarget.style.color = travelieTheme.colors.text.secondary}
                  >
                    <XCircle style={{ width: '24px', height: '24px' }} />
                  </button>
                </div>
              </div>
            </div>
            
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Agent Information */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-medium text-gray-900 mb-3 flex items-center">
                    <User className="w-4 h-4 mr-2" />
                    Agent Information
                  </h3>
                  <div className="space-y-2">
                    {isEditMode ? (
                      <>
                        <EditableField
                          label="First Name"
                          value={editFormData.first_name}
                          originalValue={originalData.first_name}
                          type="text"
                          onChange={(value) => handleFieldChange('first_name', value)}
                          isEditing={editingFields.first_name}
                          onEditToggle={(editing) => handleFieldEditToggle('first_name', editing)}
                          required
                        />
                        <EditableField
                          label="Last Name"
                          value={editFormData.last_name}
                          originalValue={originalData.last_name}
                          type="text"
                          onChange={(value) => handleFieldChange('last_name', value)}
                          isEditing={editingFields.last_name}
                          onEditToggle={(editing) => handleFieldEditToggle('last_name', editing)}
                          required
                        />
                        <EditableField
                          label="Email"
                          value={editFormData.email}
                          originalValue={originalData.email}
                          type="email"
                          onChange={(value) => handleFieldChange('email', value)}
                          isEditing={editingFields.email}
                          onEditToggle={(editing) => handleFieldEditToggle('email', editing)}
                          required
                        />
                        <EditableField
                          label="Phone"
                          value={editFormData.phone}
                          originalValue={originalData.phone}
                          type="tel"
                          onChange={(value) => handleFieldChange('phone', value)}
                          isEditing={editingFields.phone}
                          onEditToggle={(editing) => handleFieldEditToggle('phone', editing)}
                        />
                      </>
                    ) : (
                      <>
                        {renderFieldWithOriginal('First Name', selectedCommission.first_name, 'first_name')}
                        {renderFieldWithOriginal('Last Name', selectedCommission.last_name, 'last_name')}
                        {renderFieldWithOriginal('Email', selectedCommission.email, 'email')}
                        {renderFieldWithOriginal('Phone', selectedCommission.phone, 'phone')}
                      </>
                    )}
                  </div>
                </div>

                {/* Guest Information */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-medium text-gray-900 mb-3 flex items-center">
                    <User className="w-4 h-4 mr-2" />
                    Guest Information
                  </h3>
                  <div className="space-y-2">
                    {isEditMode ? (
                      <>
                        <EditableField
                          label="Guest First Name"
                          value={editFormData.guest_first_name}
                          originalValue={originalData.guest_first_name}
                          type="text"
                          onChange={(value) => handleFieldChange('guest_first_name', value)}
                          isEditing={editingFields.guest_first_name}
                          onEditToggle={(editing) => handleFieldEditToggle('guest_first_name', editing)}
                        />
                        <EditableField
                          label="Guest Last Name"
                          value={editFormData.guest_last_name}
                          originalValue={originalData.guest_last_name}
                          type="text"
                          onChange={(value) => handleFieldChange('guest_last_name', value)}
                          isEditing={editingFields.guest_last_name}
                          onEditToggle={(editing) => handleFieldEditToggle('guest_last_name', editing)}
                        />
                        <EditableField
                          label="Booking Date"
                          value={editFormData.booking_date}
                          originalValue={originalData.booking_date ? originalData.booking_date.split('T')[0] : ''}
                          type="date"
                          onChange={(value) => handleFieldChange('booking_date', value)}
                          isEditing={editingFields.booking_date}
                          onEditToggle={(editing) => handleFieldEditToggle('booking_date', editing)}
                        />
                        <EditableField
                          label="Travel Date"
                          value={editFormData.travel_date}
                          originalValue={originalData.travel_date ? originalData.travel_date.split('T')[0] : ''}
                          type="date"
                          onChange={(value) => handleFieldChange('travel_date', value)}
                          isEditing={editingFields.travel_date}
                          onEditToggle={(editing) => handleFieldEditToggle('travel_date', editing)}
                        />
                      </>
                    ) : (
                      <>
                        {renderFieldWithOriginal('Guest First Name', selectedCommission.guest_first_name, 'guest_first_name')}
                        {renderFieldWithOriginal('Guest Last Name', selectedCommission.guest_last_name, 'guest_last_name')}
                        {renderFieldWithOriginal('Booking Date', selectedCommission.booking_date ? formatDateForDisplay(selectedCommission.booking_date) : '', 'booking_date')}
                        {renderFieldWithOriginal('Travel Date', selectedCommission.travel_date ? formatDateForDisplay(selectedCommission.travel_date) : '', 'travel_date')}
                      </>
                    )}
                  </div>
                </div>

                {/* Booking Details */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-medium text-gray-900 mb-3 flex items-center">
                    <FileText className="w-4 h-4 mr-2" />
                    Booking Details
                  </h3>
                  <div className="space-y-2">
                    {isEditMode ? (
                      <>
                        <EditableField
                          label="Reservation Number"
                          value={editFormData.reservation_number}
                          originalValue={originalData.reservation_number}
                          type="text"
                          onChange={(value) => handleFieldChange('reservation_number', value)}
                          isEditing={editingFields.reservation_number}
                          onEditToggle={(editing) => handleFieldEditToggle('reservation_number', editing)}
                          required
                        />
                        <EditableField
                          label="Supplier"
                          value={editFormData.supplier}
                          originalValue={originalData.supplier}
                          type="text"
                          onChange={(value) => handleFieldChange('supplier', value)}
                          isEditing={editingFields.supplier}
                          onEditToggle={(editing) => handleFieldEditToggle('supplier', editing)}
                          required
                        />
                        <EditableField
                          label="Gross Commission"
                          value={editFormData.gross_commission}
                          originalValue={originalData.gross_commission}
                          type="number"
                          onChange={(value) => handleFieldChange('gross_commission', value)}
                          isEditing={editingFields.gross_commission}
                          onEditToggle={(editing) => handleFieldEditToggle('gross_commission', editing)}
                          required
                        />
                        <EditableField
                          label="Agent Commission"
                          value={editFormData.agent_commission}
                          originalValue={originalData.agent_commission}
                          type="number"
                          onChange={(value) => handleFieldChange('agent_commission', value)}
                          isEditing={editingFields.agent_commission}
                          onEditToggle={(editing) => handleFieldEditToggle('agent_commission', editing)}
                          placeholder="Auto-calculated if left empty"
                        />
                      </>
                    ) : (
                      <>
                        {renderFieldWithOriginal('Reservation #', selectedCommission.reservation_number, 'reservation_number')}
                        {renderFieldWithOriginal('Supplier', selectedCommission.supplier, 'supplier')}
                        {renderFieldWithOriginal('Gross Commission', selectedCommission.gross_commission ? `$${selectedCommission.gross_commission}` : '', 'gross_commission')}
                        {renderFieldWithOriginal('Agent Commission', selectedCommission.agent_commission ? `$${selectedCommission.agent_commission}` : '', 'agent_commission')}
                      </>
                    )}
                  </div>
                </div>

                {/* Status Display */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-medium text-gray-900 mb-3 flex items-center">
                    <DollarSign className="w-4 h-4 mr-2" />
                    Status
                  </h3>
                  <div className="space-y-2">
                    <p className="text-sm">
                      <span className="font-medium">Current Status:</span> 
                      <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${getStatusColor(selectedCommission.status)}-100 text-${getStatusColor(selectedCommission.status)}-800`}>
                        {selectedCommission.status}
                      </span>
                    </p>
                  </div>
                </div>
              </div>

              {selectedCommission.review_bonus_claim_id && (
                <div style={{ marginTop: '24px', padding: '16px', borderRadius: '10px', border: '2px solid #F59E0B', background: '#FFFBEB' }}>
                  <h3 style={{ margin: '0 0 12px', color: '#92400E' }}>$5 Review Bonus</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: '8px', color: '#374151', fontSize: '14px' }}>
                    <div><strong>Status:</strong> {selectedCommission.review_bonus_status}</div>
                    <div><strong>Review name:</strong> {selectedCommission.review_bonus_name}</div>
                    <div><strong>Bonus:</strong> ${Number(selectedCommission.review_bonus_amount || 5).toFixed(2)}</div>
                    <div><strong>Applied:</strong> {selectedCommission.review_bonus_applied ? 'Yes' : 'No'}</div>
                  </div>
                  {selectedCommission.review_bonus_rejection_reason && <div style={{ marginTop: '10px', color: '#991B1B' }}><strong>Rejection reason:</strong> {selectedCommission.review_bonus_rejection_reason}</div>}
                  <div style={{ display: 'flex', gap: '8px', marginTop: '14px', flexWrap: 'wrap' }}>
                    <button type="button" onClick={() => viewReviewBonusScreenshot(selectedCommission.review_bonus_claim_id)} style={getButtonStyle('info', 'small')}>View Screenshot</button>
                    {selectedCommission.review_bonus_status === 'pending' && (
                      <>
                        <button type="button" onClick={() => handleReviewBonusDecision({ id: selectedCommission.review_bonus_claim_id, commission_id: selectedCommission.id, bonus_amount: selectedCommission.review_bonus_amount || 5 }, 'approved')} disabled={reviewBonusLoading} style={getButtonStyle('success', 'small')}>Approve $5</button>
                        <button type="button" onClick={() => handleReviewBonusDecision({ id: selectedCommission.review_bonus_claim_id, commission_id: selectedCommission.id, bonus_amount: selectedCommission.review_bonus_amount || 5 }, 'rejected')} disabled={reviewBonusLoading} style={getButtonStyle('danger', 'small')}>Reject Proof</button>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Notes Section */}
              <div className="mt-6">
                {isEditMode ? (
                  <>
                    <EditableField
                      label="Commission Notes"
                      value={editFormData.notes}
                      originalValue={originalData.notes}
                      type="textarea"
                      onChange={(value) => handleFieldChange('notes', value)}
                      isEditing={editingFields.notes}
                      onEditToggle={(editing) => handleFieldEditToggle('notes', editing)}
                      placeholder="Commission notes..."
                    />
                  </>
                ) : (
                  <>
                    <div className="bg-gray-50 rounded-lg p-4 mb-4">
                      <h3 className="font-medium text-gray-900 mb-2">Agent Notes</h3>
                      {renderFieldWithOriginal('Notes', selectedCommission.notes, 'notes')}
                    </div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Admin Notes
                    </label>
                    <textarea
                      id="admin-notes"
                      rows={4}
                      defaultValue={selectedCommission.admin_notes || ''}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Add notes about this commission..."
                    />
                  </>
                )}
              </div>

              {/* Action Buttons */}
              <div className="mt-6 flex justify-end space-x-3">
                {isEditMode ? (
                  <>
                    <button
                      onClick={handleCancelEdit}
                      className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
                    >
                      Cancel Edit
                    </button>
                    <button
                      onClick={handleSaveAllChanges}
                      disabled={!hasChanges() || isSaving}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      {isSaving ? 'Saving...' : 'Save Changes'}
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => setShowDetailModal(false)}
                      className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
                    >
                      Close
                    </button>
                    <button
                      onClick={() => {
                        const notes = document.getElementById('admin-notes').value;
                        handleUpdateNotes(selectedCommission.id, notes);
                      }}
                      className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center"
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      Save Notes
                    </button>
                    <button
                      onClick={handleEditCommission}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      Edit Commission
                    </button>
                    <button
                      onClick={() => {
                        const notes = document.getElementById('admin-notes').value;
                        handleUpdateStatus(selectedCommission.id, 'approved', notes);
                      }}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Approve
                    </button>
                    <button
                      onClick={() => {
                        const notes = document.getElementById('admin-notes').value;
                        handleUpdateStatus(selectedCommission.id, 'processing', notes);
                      }}
                      className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center"
                    >
                      <TrendingUp className="w-4 h-4 mr-2" />
                      Mark Processing
                    </button>
                    <button
                      onClick={() => {
                        const notes = document.getElementById('admin-notes').value;
                        handleUpdateStatus(selectedCommission.id, 'denied', notes);
                      }}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center"
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Deny
                    </button>
                    <button
                      onClick={() => {
                        const notes = document.getElementById('admin-notes').value;
                        handleUpdateStatus(selectedCommission.id, 'on_hold', notes);
                      }}
                      className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 flex items-center"
                    >
                      <AlertCircle className="w-4 h-4 mr-2" />
                      Hold
                    </button>
                    <button
                      onClick={() => {
                        const notes = document.getElementById('admin-notes').value;
                        handleUpdateStatus(selectedCommission.id, 'paid', notes);
                      }}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center"
                    >
                      <DollarSign className="w-4 h-4 mr-2" />
                      Mark as Paid
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {showMessagingModal && selectedCommissionForMessaging && (
        <AdminCommissionMessagingModal 
          commission={selectedCommissionForMessaging} 
          onClose={() => {
            setShowMessagingModal(false);
            setPrefilledMessage('');
          }} 
          onMessageSent={handleMessageSent} 
          prefilledMessage={prefilledMessage}
        />
      )}
      {showUploadModal && (
        <CommissionUploadModal 
          isOpen={showUploadModal}
          onClose={() => setShowUploadModal(false)} 
          onUploadComplete={handleUploadComplete} 
        />
      )}
      {showExportModal && (
        <CommissionExportModal 
          isOpen={showExportModal}
          onClose={() => setShowExportModal(false)} 
          currentFilters={filters}
          searchTerm={searchTerm}
        />
      )}
      {showPayConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <h2 className="text-xl font-semibold">Confirm Payment</h2>
            </div>
            <div className="p-6">
              {paymentSummaryLoading ? (
                <p className="text-sm text-gray-600 mb-4">Calculating payment batch…</p>
              ) : paymentSummary ? (
                <>
                  <p className="text-sm text-gray-600 mb-4">
                    Review this payment batch before continuing. This action will download the payment CSV and mark all included commissions as Paid.
                  </p>
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="rounded-lg bg-gray-50 p-3"><div className="text-xs text-gray-500">Commissions</div><div className="text-xl font-semibold">{paymentSummary.commissionCount}</div></div>
                    <div className="rounded-lg bg-gray-50 p-3"><div className="text-xs text-gray-500">Agents</div><div className="text-xl font-semibold">{paymentSummary.agentCount}</div></div>
                    <div className="rounded-lg bg-gray-50 p-3"><div className="text-xs text-gray-500">Emails</div><div className="text-xl font-semibold">{paymentSummary.emailCount}</div></div>
                    <div className="rounded-lg bg-gray-50 p-3"><div className="text-xs text-gray-500">Total</div><div className="text-xl font-semibold">${Number(paymentSummary.totalAmount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div></div>
                  </div>
                  <p className="text-xs text-gray-500 mb-4">Each agent receives one consolidated email listing all commissions included in this batch.</p>
                </>
              ) : null}
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => { setShowPayConfirmModal(false); setPaymentSummary(null); }}
                  disabled={paymentProcessing}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handlePayApprovedCommissions}
                  disabled={paymentSummaryLoading || paymentProcessing || !paymentSummary?.commissionCount}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center disabled:opacity-50"
                >
                  {paymentProcessing ? (
                    <svg className="w-4 h-4 mr-2 animate-spin" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  ) : (
                    <DollarSign className="w-4 h-4 mr-2" />
                  )}
                  Pay & Export
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showEditReasonModal && (
        <EditReasonModal
          isOpen={showEditReasonModal}
          onClose={() => setShowEditReasonModal(false)}
          onSave={handleEditReasonSave}
          changedFields={changedFields.map(field => field.field)}
        />
      )}
    </div>
  );
};

export default CommissionsManagement;
